/**
 * CallScript V2 Unified Styles
 * All values in rem (base font-size: 16px)
 * Conversion: px / 16 = rem
 */

// ============ THEME CONFIGURATION ============

export const callScriptColors = {
  primary: {
    lighter: '#C8FAD6',
    light: '#5BE49B',
    main: '#00A76F',
    dark: '#007867',
  },
  info: {
    lighter: '#CAFDF5',
    light: '#61F3F3',
    main: '#00B8D9',
    dark: '#006C9C',
  },
  warning: {
    lighter: '#FFF5CC',
    light: '#FFD666',
    main: '#FFAB00',
    dark: '#B76E00',
  },
  success: {
    lighter: '#D3FCD2',
    light: '#77ED8B',
    main: '#22C55E',
    dark: '#118D57',
  },
  error: {
    lighter: '#FFE9D5',
    light: '#FFAC82',
    main: '#FF5630',
    dark: '#B71D18',
  },
  purple: {
    lighter: '#EBD6FD',
    light: '#B985F4',
    main: '#7635DC',
    dark: '#431A9E',
  },
  grey: {
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  text: {
    primary: '#212B36',
    secondary: '#637381',
    disabled: '#919EAB',
  },
  background: {
    paper: '#FFFFFF',
    default: '#F4F6F8',
  },
};

export const callScriptShadows = {
  card: '0 0 2px 0 rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
  small: '0 1px 2px rgba(0,0,0,0.05)',
  medium: '0 4px 12px rgba(0,0,0,0.1)',
};

export const callScriptRadius = {
  sm: '0.5rem',    // 8px
  md: '0.75rem',   // 12px
  lg: '1rem',      // 16px
  full: '9999px',
};

export const sectionBadgeColors = {
  s1: { bg: 'rgba(0,167,111,0.1)', color: callScriptColors.primary.dark },
  s2: { bg: 'rgba(0,184,217,0.1)', color: callScriptColors.info.dark },
  s3: { bg: 'rgba(255,171,0,0.1)', color: callScriptColors.warning.dark },
  s4: { bg: 'rgba(118,53,220,0.1)', color: callScriptColors.purple.dark },
  s5: { bg: 'rgba(255,86,48,0.1)', color: callScriptColors.error.dark },
  s6: { bg: 'rgba(0,167,111,0.1)', color: callScriptColors.primary.dark },
  s7: { bg: 'rgba(34,197,94,0.1)', color: callScriptColors.success.dark },
};

export const callTypeConfig = {
  inbound: {
    color: callScriptColors.info.main,
    bgLight: callScriptColors.info.lighter,
    colorDark: callScriptColors.info.dark,
    icon: 'call_received',
    label: 'Inbound',
  },
  outbound: {
    color: callScriptColors.primary.main,
    bgLight: callScriptColors.primary.lighter,
    colorDark: callScriptColors.primary.dark,
    icon: 'call_made',
    label: 'Outbound',
  },
};

const { primary, info, warning, success, error, purple, grey, text, background } = callScriptColors;

// ============ LAYOUT STYLES ============

export const appContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  bgcolor: background.default,
};

export const mainContainerSx = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

export const scriptPanelSx = {
  flex: '0 0 70%',
  maxWidth: '70%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: background.default,
  borderRight: `1px solid ${grey[200]}`,
  overflow: 'hidden',
  '@media (max-width: 1024px)': {
    flex: '0 0 60%',
    maxWidth: '60%',
  },
  '@media (max-width: 768px)': {
    flex: 1,
    width: '100%',
    maxWidth: '100%',
  },
};

export const contextPanelSx = {
  flex: '0 0 30%',
  minWidth: '18.75rem', // 300px
  display: 'flex',
  flexDirection: 'column',
  bgcolor: background.paper,
  overflowY: 'auto',
  '@media (max-width: 1024px)': {
    flex: '0 0 40%',
    minWidth: '17.5rem', // 280px
  },
  '@media (max-width: 768px)': {
    position: 'fixed',
    top: 0,
    right: '-100%',
    width: '85%',
    maxWidth: '20rem', // 320px
    minWidth: 'auto',
    height: '100vh',
    zIndex: 100,
    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
    transition: 'right 0.3s ease',
    '&.open': {
      right: 0,
    },
  },
};

// ============ HEADER STYLES ============

export const headerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.875rem 1.5rem', // 14px 24px
  bgcolor: background.paper,
  borderBottom: `1px solid ${grey[200]}`,
  '@media (max-width: 768px)': {
    padding: '0.625rem 1rem', // 10px 16px
  },
};

export const headerLeftSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.875rem', // 14px
};

export const headerIconSx = (type = 'inbound') => ({
  width: '2.75rem', // 44px
  height: '2.75rem',
  borderRadius: callScriptRadius.sm,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: type === 'inbound' ? 'rgba(0,184,217,0.12)' : 'rgba(0,167,111,0.12)',
  color: type === 'inbound' ? info.main : primary.main,
  '@media (max-width: 768px)': {
    width: '2.25rem', // 36px
    height: '2.25rem',
  },
});

export const headerTitleSx = {
  '& h1': {
    fontSize: '1.0625rem', // 17px
    fontWeight: 600,
    color: text.primary,
    margin: 0,
    '@media (max-width: 768px)': {
      fontSize: '0.875rem', // 14px
    },
  },
  '& p': {
    fontSize: '0.8125rem', // 13px
    color: text.secondary,
    marginTop: '0.125rem', // 2px
    margin: 0,
    '@media (max-width: 768px)': {
      fontSize: '0.6875rem', // 11px
    },
  },
};

export const headerBadgeSx = (type = 'inbound') => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.375rem 0.875rem', // 6px 14px
  borderRadius: '1.25rem', // 20px
  fontSize: '0.75rem', // 12px
  fontWeight: 600,
  marginLeft: '0.875rem', // 14px
  bgcolor: type === 'inbound' ? info.lighter : primary.lighter,
  color: type === 'inbound' ? info.dark : primary.dark,
  '@media (max-width: 1024px)': {
    display: 'none',
  },
});

export const headerRightSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem', // 16px
};

export const progressMiniSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem', // 12px
  padding: '0.5rem 1rem', // 8px 16px
  bgcolor: grey[100],
  borderRadius: '1.5rem', // 24px
  '@media (max-width: 768px)': {
    padding: '0.375rem 0.625rem', // 6px 10px
  },
};

export const saveIndicatorSx = (status = 'saved') => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.375rem 0.75rem', // 6px 12px
  bgcolor: grey[100],
  borderRadius: '1rem', // 16px
  fontSize: '0.6875rem', // 11px
  color: (() => {
    if (status === 'saving') return warning.dark;
    if (status === 'saved') return success.dark;
    if (status === 'waiting_for_lead') return warning.dark;
    return text.secondary;
  })(),
});

// ============ SECTION TABS STYLES ============

export const sectionTabsContainerSx = {
  display: 'flex',
  gap: '0.5rem', // 8px
  padding: '0.875rem 1.5rem', // 14px 24px
  bgcolor: background.paper,
  borderBottom: `1px solid ${grey[200]}`,
  overflowX: 'auto',
  flexWrap: 'nowrap',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '@media (max-width: 768px)': {
    padding: '0.5rem 0.75rem', // 8px 12px
    gap: '0.25rem', // 4px
  },
};

export const sectionTabSx = (isActive, isCompleted, hasSkipped) => ({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.625rem', // 10px
  padding: '0.75rem 1rem', // 12px 16px
  borderRadius: '0.625rem', // 10px
  fontSize: '0.8125rem', // 13px
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'all 0.2s',
  border: 'none',
  maxWidth: '12.5rem', // 200px
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  bgcolor: (() => {
    if (isActive) return primary.main;
    if (isCompleted && hasSkipped) return warning.lighter;
    if (isCompleted) return primary.lighter;
    return grey[100];
  })(),
  color: (() => {
    if (isActive) return '#fff';
    if (isCompleted && hasSkipped) return warning.dark;
    if (isCompleted) return primary.dark;
    return text.secondary;
  })(),
  '&:hover': {
    bgcolor: isActive ? primary.main : grey[200],
    color: isActive ? '#fff' : text.primary,
  },
  '& span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '8.75rem', // 140px
  },
  '@media (max-width: 768px)': {
    padding: '0.375rem 0.625rem', // 6px 10px
    fontSize: '0.6875rem', // 11px
    gap: '0.375rem', // 6px
    maxWidth: '9.375rem', // 150px
  },
});

export const sectionTabNumSx = (isActive, isCompleted, hasSkipped) => ({
  width: '1.375rem', // 22px
  height: '1.375rem',
  minWidth: '1.375rem',
  minHeight: '1.375rem',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: '0.6875rem', // 11px
  fontWeight: 700,
  bgcolor: (() => {
    if (isActive) return 'rgba(255,255,255,0.3)';
    if (isCompleted && hasSkipped) return warning.main;
    if (isCompleted) return primary.main;
    return 'rgba(0,0,0,0.08)';
  })(),
  color: isCompleted || isActive ? '#fff' : 'inherit',
  '@media (max-width: 768px)': {
    width: '1.125rem', // 18px
    height: '1.125rem',
    minWidth: '1.125rem',
    minHeight: '1.125rem',
    fontSize: '0.5625rem', // 9px
  },
});

// ============ QUESTION PROGRESS STYLES ============

export const questionProgressContainerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '0.5rem', // 8px
  padding: '0.75rem 1.5rem', // 12px 24px
  bgcolor: background.paper,
  borderBottom: `1px solid ${grey[200]}`,
  overflowX: 'auto',
  overflowY: 'hidden',
  flexWrap: 'nowrap',
  scrollBehavior: 'smooth',
  /* Hide scrollbar but keep functionality */
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE/Edge
  '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
  '@media (max-width: 768px)': {
    padding: '0.5rem 0.75rem', // 8px 12px
    gap: '0.375rem', // 6px
  },
};

export const questionProgressLabelSx = {
  fontSize: '0.75rem', // 12px
  color: text.secondary,
  marginRight: '0.5rem', // 8px
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '@media (max-width: 768px)': {
    fontSize: '0.625rem', // 10px
    marginRight: '0.25rem', // 4px
  },
};

const getQuestionDotBorderColor = (isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic) => {
  if (isActive) return primary.main;
  if (isCompleted) return success.main;
  if (isAutoFilled) return purple.light;
  if (isSkipped) return warning.main;
  if (hasConditionalLogic) return purple.main;
  return grey[300];
};

const getQuestionDotBgColor = (isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic) => {
  if (isActive) return primary.main;
  if (isCompleted) return success.main;
  if (isAutoFilled) return purple.lighter;
  if (isSkipped) return warning.lighter;
  if (hasConditionalLogic) return purple.lighter;
  return background.paper;
};

const getQuestionDotColor = (isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic) => {
  if (isActive || isCompleted) return '#fff';
  if (isAutoFilled) return purple.dark;
  if (isSkipped) return warning.dark;
  if (hasConditionalLogic) return purple.dark;
  return text.secondary;
};

export const questionDotSx = (isActive, isCompleted, isSkipped, hasNote, isAutoFilled, hasConditionalLogic = false) => ({
  position: 'relative',
  width: '1.75rem', // 28px
  minWidth: '1.75rem', // prevent shrinking
  height: '1.75rem',
  borderRadius: '50%',
  border: `2px solid ${getQuestionDotBorderColor(isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic)}`,
  bgcolor: getQuestionDotBgColor(isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic),
  color: getQuestionDotColor(isActive, isCompleted, isSkipped, isAutoFilled, hasConditionalLogic),
  fontSize: '0.6875rem', // 11px
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s',
  transform: isActive ? 'scale(1.1)' : 'scale(1)',
  // Purple ring for cards with conditional logic — drawn via box-shadow so it doesn't affect layout.
  // Only applies when conditional logic exists AND the dot isn't already in a styled state (active/completed/skipped/autoFilled),
  // so we always have at least the purple border above for those un-styled cases.
  ...(hasConditionalLogic && !isActive && !isCompleted && !isSkipped && !isAutoFilled && {
    boxShadow: `0 0 0 2px ${purple.lighter}`,
  }),
  '&:hover': {
    borderColor: hasConditionalLogic ? purple.dark : primary.main,
    // eslint-disable-next-line no-nested-ternary
    color: isActive || isCompleted ? '#fff' : (hasConditionalLogic ? purple.dark : primary.main),
  },
  '&::before': hasNote ? {
    content: '""',
    position: 'absolute',
    top: '-0.125rem', // -2px
    right: '-0.125rem',
    width: '0.5rem', // 8px
    height: '0.5rem',
    bgcolor: info.main,
    borderRadius: '50%',
    border: `2px solid ${background.paper}`,
  } : {},
  '@media (max-width: 768px)': {
    width: '1.375rem', // 22px
    height: '1.375rem',
    fontSize: '0.5625rem', // 9px
  },
});

// ============ CARD AREA STYLES ============

export const cardAreaSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '1.75rem 2rem', // 28px 32px
  overflowY: 'auto',
  minHeight: 0,
  '@media (max-width: 768px)': {
    padding: '1rem', // 16px
  },
};

// ============ SCRIPT CARD STYLES ============

export const scriptCardSx = (wasSkipped = false, hasRequiredFields = false) => ({
  bgcolor: background.paper,
  borderRadius: callScriptRadius.lg,
  boxShadow: callScriptShadows.card,
  width: '100%',
  maxWidth: '47.5rem', // 760px
  marginBottom: '1.75rem', // 28px
  animation: 'slideUp 0.25s ease-out',
  '@keyframes slideUp': {
    from: { opacity: 0, transform: 'translateY(0.75rem)' }, // 12px
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  ...(wasSkipped && {
    border: `2px solid ${hasRequiredFields ? error.main : warning.main}`,
  }),
});

export const skippedBannerSx = (hasRequiredFields = false) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem', // 10px
  padding: '0.75rem 1.25rem', // 12px 20px
  bgcolor: hasRequiredFields ? error.lighter : warning.lighter,
  borderRadius: `${callScriptRadius.lg} ${callScriptRadius.lg} 0 0`,
  borderBottom: `1px solid ${hasRequiredFields ? 'rgba(255,86,48,0.3)' : 'rgba(255,171,0,0.3)'}`,
  '& .icon': {
    color: hasRequiredFields ? error.dark : warning.dark,
    fontSize: '1.25rem', // 20px
  },
  '& span': {
    fontSize: '0.8125rem', // 13px
    fontWeight: 500,
    color: hasRequiredFields ? error.dark : warning.dark,
  },
});

export const cardHeaderSx = {
  padding: '1.5rem 1.75rem 1.25rem', // 24px 28px 20px
  borderBottom: `1px solid ${grey[100]}`,
  '@media (max-width: 768px)': {
    padding: '0.875rem 1rem 0.75rem', // 14px 16px 12px
  },
};

export const cardBadgeSx = (badgeClass = 's1') => {
  const colors = sectionBadgeColors[badgeClass] || sectionBadgeColors.s1;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem', // 6px
    padding: '0.3125rem 0.75rem', // 5px 12px
    borderRadius: '0.625rem', // 10px
    fontSize: '0.6875rem', // 11px
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.01875rem', // 0.3px
    marginBottom: '0.75rem', // 12px
    bgcolor: colors.bg,
    color: colors.color,
    '@media (max-width: 768px)': {
      fontSize: '0.5625rem', // 9px
      padding: '0.1875rem 0.5rem', // 3px 8px
    },
  };
};

export const cardTitleSx = {
  fontSize: '1.25rem', // 20px
  fontWeight: 600,
  color: text.primary,
  marginBottom: '0.25rem', // 4px
  '@media (max-width: 768px)': {
    fontSize: '0.9375rem', // 15px
  },
};

export const cardSubtitleSx = {
  fontSize: '0.875rem', // 14px
  color: text.secondary,
  '@media (max-width: 768px)': {
    fontSize: '0.75rem', // 12px
  },
};

export const cardBodySx = {
  padding: '1.5rem 1.75rem 1.75rem', // 24px 28px 28px
  '@media (max-width: 768px)': {
    padding: '0.875rem 1rem 1.125rem', // 14px 16px 18px
  },
};

// ============ SCROLLABLE VIEW STYLES ============

export const scrollableCardAreaSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '1.75rem 2rem',
  overflowY: 'auto',
  minHeight: 0,
  scrollBehavior: 'smooth',
  '@media (max-width: 768px)': {
    padding: '1rem',
  },
};

export const scrollableScriptCardSx = (isActive = false, wasSkipped = false, hasRequiredFields = false) => ({
  bgcolor: background.paper,
  borderRadius: callScriptRadius.lg,
  boxShadow: isActive ? callScriptShadows.card : callScriptShadows.small,
  width: '100%',
  maxWidth: '47.5rem',
  marginBottom: '1rem',
  transition: 'all 0.25s ease-out',
  cursor: isActive ? 'default' : 'pointer',
  opacity: isActive ? 1 : 0.75,
  borderLeft: isActive ? `4px solid ${primary.main}` : '4px solid transparent',
  '&:hover': {
    ...(!isActive && {
      opacity: 0.9,
      boxShadow: callScriptShadows.card,
    }),
  },
  ...(wasSkipped && {
    border: `2px solid ${hasRequiredFields ? error.main : warning.main}`,
    borderLeft: isActive
      ? `4px solid ${hasRequiredFields ? error.main : warning.main}`
      : `2px solid ${hasRequiredFields ? error.main : warning.main}`,
  }),
});

export const scrollableCardStepIndicatorSx = (isActive = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%',
  fontSize: '0.75rem',
  fontWeight: 600,
  bgcolor: isActive ? primary.main : grey[200],
  color: isActive ? '#fff' : text.secondary,
  flexShrink: 0,
  transition: 'all 0.2s ease',
});

// ============ SAY BLOCK STYLES ============

export const sayBlockSx = (variant = 'primary') => ({
  position: 'relative',
  background:
    variant === 'info'
      ? 'linear-gradient(135deg, rgba(0,184,217,0.06) 0%, rgba(0,184,217,0.02) 100%)'
      : 'linear-gradient(135deg, rgba(0,167,111,0.06) 0%, rgba(0,167,111,0.02) 100%)',
  border: `1px solid ${variant === 'info' ? 'rgba(0,184,217,0.12)' : 'rgba(0,167,111,0.12)'}`,
  borderLeft: `4px solid ${variant === 'info' ? info.main : primary.main}`,
  borderRadius: `0 ${callScriptRadius.md} ${callScriptRadius.md} 0`,
  padding: '1.125rem 1.25rem', // 18px 20px
  marginBottom: '1.25rem', // 20px
  '@media (max-width: 768px)': {
    padding: '0.75rem 0.875rem', // 12px 14px
    marginBottom: '0.875rem', // 14px
  },
});

export const sayTagSx = (variant = 'primary') => ({
  position: 'absolute',
  top: '-0.625rem', // -10px
  left: '1rem', // 16px
  padding: '0.1875rem 0.625rem', // 3px 10px
  bgcolor: variant === 'info' ? info.main : primary.main,
  borderRadius: '0.625rem', // 10px
  fontSize: '0.625rem', // 10px
  fontWeight: 600,
  color: '#fff',
  textTransform: 'uppercase',
  letterSpacing: '0.01875rem', // 0.3px
  '@media (max-width: 768px)': {
    fontSize: '0.5625rem', // 9px
    padding: '0.125rem 0.5rem', // 2px 8px
  },
});

export const sayTextSx = {
  fontSize: '0.9375rem', // 15px
  color: text.primary,
  lineHeight: 1.6,
  marginTop: '0.25rem', // 4px
  fontWeight: 600,
  '@media (max-width: 768px)': {
    fontSize: '0.8125rem', // 13px
    lineHeight: 1.5,
  },
};

export const variableHighlightSx = {
  fontWeight: 600,
  padding: '0.125rem 0.5rem', // 2px 8px
  borderRadius: '0.25rem', // 4px
  bgcolor: 'rgba(0,184,217,0.15)',
  color: info.dark,
};

// ============ TIP BLOCK STYLES ============

export const tipBlockSx = {
  display: 'flex',
  gap: '0.625rem', // 10px
  padding: '0.875rem 1rem', // 14px 16px
  bgcolor: 'rgba(255,171,0,0.08)',
  borderRadius: callScriptRadius.sm,
  marginBottom: '1.25rem', // 20px
  '@media (max-width: 768px)': {
    padding: '0.625rem 0.75rem', // 10px 12px
  },
};

export const tipIconSx = {
  color: warning.dark,
  fontSize: '1.25rem', // 20px
};

export const tipTextSx = {
  fontSize: '0.8125rem', // 13px
  color: warning.dark,
  lineHeight: 1.5,
  '@media (max-width: 768px)': {
    fontSize: '0.75rem', // 12px
  },
};

// ============ FORM STYLES ============

export const formRowSx = {
  display: 'flex',
  gap: '0.875rem', // 14px
  marginBottom: '1.125rem', // 18px
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '0.75rem', // 12px
  },
};

export const formGroupSx = (width = 'full') => ({
  flex: width === 'narrow' ? '0 0 6.25rem' : 1, // 100px
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem', // 6px
  '@media (max-width: 768px)': {
    flex: 1,
  },
});

export const formLabelSx = {
  fontSize: '0.75rem', // 12px
  fontWeight: 500,
  color: text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem', // 4px
  '& .req': {
    color: error.main,
    fontSize: '0.875rem', // 14px - larger for visibility
    fontWeight: 700,
    lineHeight: 1,
  },
};

// Required field indicator for inputs
export const requiredFieldSx = {
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    bgcolor: error.main,
    borderRadius: '8px 0 0 8px',
  },
};

// Helper to get input border color
const getInputBorderColor = (isFilled, isRequired) => {
  if (isFilled) return info.main;
  if (isRequired) return error.light;
  return grey[300];
};

// Helper to get input background color
const getInputBgColor = (isFilled, isRequired) => {
  if (isFilled) return 'rgba(0,184,217,0.06)';
  if (isRequired) return 'rgba(255,86,48,0.02)';
  return background.paper;
};

export const formInputSx = (isFilled = false, isRequired = false) => ({
  padding: '0.6875rem 0.875rem', // 11px 14px
  border: `1.5px solid ${getInputBorderColor(isFilled, isRequired && !isFilled)}`,
  borderRadius: callScriptRadius.sm,
  fontSize: '0.875rem', // 14px
  color: isFilled ? info.dark : text.primary,
  bgcolor: getInputBgColor(isFilled, isRequired && !isFilled),
  fontWeight: isFilled ? 500 : 400,
  transition: 'all 0.2s',
  position: 'relative',
  // Left accent bar for required fields when not filled
  ...(isRequired && !isFilled && {
    borderLeft: `3px solid ${error.main}`,
  }),
  '&:focus': {
    outline: 'none',
    borderColor: primary.main,
    boxShadow: '0 0 0 3px rgba(0,167,111,0.12)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

// ============ CHOICE GRID STYLES ============

export const choiceGridSx = (isSingle = false) => ({
  display: 'grid',
  gridTemplateColumns: isSingle ? '1fr' : 'repeat(2, 1fr)',
  gap: '0.625rem', // 10px
  marginBottom: '1.25rem', // 20px
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

export const choiceItemSx = (isSelected = false) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem', // 10px
  padding: '0.875rem 1rem', // 14px 16px
  border: `2px solid ${isSelected ? primary.main : grey[200]}`,
  borderRadius: callScriptRadius.md,
  bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : background.paper,
  cursor: 'pointer',
  transition: 'all 0.15s',
  '&:hover': {
    borderColor: isSelected ? primary.main : grey[300],
    bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : grey[100],
  },
});

// ============ RESPONSE BUTTONS STYLES ============

export const responseGroupSx = (isVertical = false) => ({
  display: 'flex',
  flexDirection: isVertical ? 'column' : 'row',
  gap: '0.75rem', // 12px
  marginTop: '1.25rem', // 20px
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '0.625rem', // 10px
  },
});

export const responseBtnSx = (variant = 'default', isSelected = false) => {
  const variantStyles = {
    positive: {
      borderColor: isSelected ? primary.main : grey[200],
      bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : background.paper,
      iconBg: 'rgba(0,167,111,0.15)',
      iconColor: primary.main,
    },
    negative: {
      borderColor: isSelected ? warning.main : grey[200],
      bgcolor: isSelected ? 'rgba(255,171,0,0.06)' : background.paper,
      iconBg: 'rgba(255,171,0,0.15)',
      iconColor: warning.dark,
    },
    danger: {
      borderColor: isSelected ? error.main : grey[200],
      bgcolor: isSelected ? 'rgba(255,86,48,0.06)' : background.paper,
      iconBg: 'rgba(255,86,48,0.15)',
      iconColor: error.main,
    },
    default: {
      borderColor: isSelected ? primary.main : grey[200],
      bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : background.paper,
      iconBg: grey[200],
      iconColor: text.secondary,
    },
  };

  const styles = variantStyles[variant] || variantStyles.default;

  return {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem', // 12px
    padding: '1rem 1.125rem', // 16px 18px
    border: `2px solid ${styles.borderColor}`,
    borderRadius: callScriptRadius.md,
    bgcolor: styles.bgcolor,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: styles.borderColor,
      transform: 'translateY(-1px)',
    },
    '& .icon': {
      width: '2.625rem', // 42px
      height: '2.625rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      bgcolor: styles.iconBg,
      color: styles.iconColor,
    },
    '& .text span': {
      display: 'block',
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      color: text.primary,
    },
    '& .text small': {
      fontSize: '0.75rem', // 12px
      color: text.secondary,
    },
    '@media (max-width: 768px)': {
      padding: '0.75rem 0.875rem', // 12px 14px
      '& .icon': {
        width: '2.25rem', // 36px
        height: '2.25rem',
      },
    },
  };
};

// ============ INTEREST CARDS STYLES ============

export const interestCardsContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem', // 12px
};

export const interestCardSx = (variant = 'default', isSelected = false) => {
  const variantStyles = {
    hot: {
      borderColor: isSelected ? success.main : grey[200],
      bgcolor: isSelected ? 'rgba(34,197,94,0.08)' : background.paper,
      iconBg: 'rgba(34,197,94,0.12)',
      checkColor: success.main,
    },
    warm: {
      borderColor: isSelected ? warning.main : grey[200],
      bgcolor: isSelected ? 'rgba(255,171,0,0.08)' : background.paper,
      iconBg: 'rgba(255,171,0,0.12)',
      checkColor: warning.main,
    },
    cold: {
      borderColor: isSelected ? info.main : grey[200],
      bgcolor: isSelected ? 'rgba(0,184,217,0.08)' : background.paper,
      iconBg: 'rgba(0,184,217,0.12)',
      checkColor: info.main,
    },
    default: {
      borderColor: isSelected ? primary.main : grey[200],
      bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : background.paper,
      iconBg: grey[100],
      checkColor: primary.main,
    },
  };

  const styles = variantStyles[variant] || variantStyles.default;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem', // 16px
    padding: '1.125rem 1.25rem', // 18px 20px
    border: `2px solid ${styles.borderColor}`,
    borderRadius: callScriptRadius.md,
    bgcolor: styles.bgcolor,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: grey[300],
      bgcolor: isSelected ? styles.bgcolor : grey[100],
    },
    '& .interest-icon': {
      fontSize: '2rem', // 32px
      width: '3.125rem', // 50px
      height: '3.125rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: callScriptRadius.sm,
      flexShrink: 0,
      bgcolor: styles.iconBg,
    },
    '& .interest-check': {
      color: isSelected ? styles.checkColor : grey[300],
      transition: 'all 0.15s',
    },
  };
};

// ============ OUTCOME BUTTONS STYLES ============

export const outcomeButtonsContainerSx = {
  display: 'flex',
  gap: '0.75rem', // 12px
  '@media (max-width: 768px)': {
    flexDirection: 'column',
  },
};

export const outcomeBtnSx = (variant = 'submit', isSelected = false) => {
  const variantStyles = {
    submit: {
      borderColor: isSelected ? grey[500] : grey[300],
      bgcolor: isSelected ? grey[100] : background.paper,
      iconColor: text.secondary,
    },
    'not-booked': {
      borderColor: isSelected ? warning.main : 'rgba(255,171,0,0.3)',
      bgcolor: isSelected ? 'rgba(255,171,0,0.08)' : background.paper,
      iconColor: warning.main,
    },
    booked: {
      borderColor: isSelected ? primary.main : 'rgba(0,167,111,0.3)',
      bgcolor: isSelected ? 'rgba(0,167,111,0.08)' : background.paper,
      iconColor: primary.main,
    },
  };

  const styles = variantStyles[variant] || variantStyles.submit;

  return {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.625rem', // 10px
    padding: '1.5rem 1rem', // 24px 16px
    border: `2px solid ${styles.borderColor}`,
    borderRadius: callScriptRadius.md,
    bgcolor: styles.bgcolor,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontFamily: 'inherit',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'scale(1.05)',
      transition: 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s ease',
    },
    ...(isSelected && {
      animation: 'outcomeBounce 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }),
    '@keyframes outcomeBounce': {
      '0%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.05)' },
      '100%': { transform: 'scale(1)' },
    },
    '& .MuiSvgIcon-root': {
      fontSize: '2rem', // 32px
      color: styles.iconColor,
    },
    '& span:last-child': {
      fontSize: '0.8125rem', // 13px
      fontWeight: 600,
      color: text.primary,
      textAlign: 'center',
    },
  };
};

// ============ STICKY FOOTER STYLES ============

export const stickyFooterSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 2rem', // 16px 32px
  bgcolor: background.paper,
  borderTop: `1px solid ${grey[200]}`,
  flexShrink: 0,
  '@media (max-width: 768px)': {
    padding: '0.625rem 1rem', // 10px 16px
  },
};

export const footerLeftSx = {
  display: 'flex',
  gap: '0.625rem', // 10px
  '@media (max-width: 768px)': {
    gap: '0.5rem', // 8px
  },
};

export const footerRightSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem', // 12px
};

export const btnBackSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.75rem 1.25rem', // 12px 20px
  border: 'none',
  bgcolor: grey[100],
  color: text.secondary,
  fontSize: '0.875rem', // 14px
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: callScriptRadius.sm,
  transition: 'all 0.2s',
  '&:hover': {
    bgcolor: grey[200],
    color: text.primary,
  },
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  '@media (max-width: 768px)': {
    padding: '0.5rem 0.75rem', // 8px 12px
    fontSize: '0.75rem', // 12px
  },
};

export const btnSkipSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.75rem 1.25rem', // 12px 20px
  border: `1px solid ${warning.main}`,
  bgcolor: warning.lighter,
  color: warning.dark,
  fontSize: '0.875rem', // 14px
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: callScriptRadius.sm,
  transition: 'all 0.2s',
  '&:hover': {
    bgcolor: warning.light,
    borderColor: warning.dark,
  },
  '@media (max-width: 768px)': {
    padding: '0.5rem 0.75rem', // 8px 12px
    fontSize: '0.75rem', // 12px
  },
};

export const btnSaveSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.75rem 1.25rem', // 12px 20px
  border: `1px solid ${primary.main}`,
  bgcolor: primary.lighter,
  color: primary.dark,
  fontSize: '0.875rem', // 14px
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: callScriptRadius.sm,
  transition: 'all 0.2s',
  '&:hover': {
    bgcolor: primary.light,
    borderColor: primary.dark,
  },
  '@media (max-width: 768px)': {
    padding: '0.5rem 0.75rem', // 8px 12px
    fontSize: '0.75rem', // 12px
  },
};

export const btnNextSx = (isComplete = false) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem', // 8px
  padding: '0.875rem 1.75rem', // 14px 28px
  border: 'none',
  bgcolor: isComplete ? success.main : primary.main,
  color: '#fff',
  fontSize: '0.9375rem', // 15px
  fontWeight: 600,
  cursor: 'pointer',
  borderRadius: callScriptRadius.sm,
  transition: 'all 0.2s',
  '&:hover': {
    bgcolor: isComplete ? success.dark : primary.dark,
    transform: 'translateY(-1px)',
  },
  '@media (max-width: 768px)': {
    padding: '0.625rem 1rem', // 10px 16px
    fontSize: '0.8125rem', // 13px
  },
});

// ============ CONTEXT PANEL STYLES ============

export const patientHeaderSx = (type = 'inbound') => ({
  padding: '1.125rem 1.375rem', // 18px 22px
  borderBottom: `1px solid ${grey[200]}`,
  background:
    type === 'inbound'
      ? 'linear-gradient(135deg, rgba(0,184,217,0.08) 0%, transparent 100%)'
      : 'linear-gradient(135deg, rgba(0,167,111,0.08) 0%, transparent 100%)',
  '@media (max-width: 768px)': {
    padding: '0.875rem 1rem', // 14px 16px
  },
});

export const patientAvatarSx = (type = 'inbound', isUnknown = false) => ({
  width: '3.25rem', // 52px
  height: '3.25rem',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.125rem', // 18px
  fontWeight: 600,
  color: '#fff',
  bgcolor: (() => {
    if (isUnknown) return grey[400];
    if (type === 'inbound') return info.main;
    return primary.main;
  })(),
  '@media (max-width: 768px)': {
    width: '2.5rem', // 40px
    height: '2.5rem',
    fontSize: '0.875rem', // 14px
  },
});

export const patientTagSx = (variant = 'treatment') => {
  const variantStyles = {
    treatment: { bg: 'rgba(0,167,111,0.12)', color: primary.dark },
    source: { bg: 'rgba(118,53,220,0.12)', color: purple.dark },
    'type-in': { bg: info.lighter, color: info.dark },
    'type-out': { bg: primary.lighter, color: primary.dark },
  };
  const styles = variantStyles[variant] || variantStyles.treatment;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem', // 4px
    padding: '0.25rem 0.625rem', // 4px 10px
    borderRadius: '0.875rem', // 14px
    fontSize: '0.6875rem', // 11px
    fontWeight: 500,
    bgcolor: styles.bg,
    color: styles.color,
  };
};

export const contextBodySx = {
  flex: 1,
  overflowY: 'auto',
  padding: '1.125rem 1.375rem', // 18px 22px
  '@media (max-width: 768px)': {
    padding: '0.875rem 1rem', // 14px 16px
  },
};

export const progressSectionSx = {
  bgcolor: grey[100],
  border: `1px solid ${grey[200]}`,
  borderRadius: callScriptRadius.md,
  padding: '1rem', // 16px
  marginBottom: '1.25rem', // 20px
  '@media (max-width: 768px)': {
    padding: '0.75rem', // 12px
  },
};

export const dataSectionSx = {
  marginBottom: '1.25rem', // 20px
};

export const dataRowSx = {
  display: 'flex',
  padding: '0.5rem 0', // 8px 0
  borderBottom: `1px solid ${grey[100]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
};

export const notesSectionSx = {
  bgcolor: grey[100],
  border: `1px solid ${grey[200]}`,
  borderRadius: callScriptRadius.md,
  padding: '0.875rem', // 14px
};

// ============ NOTE TOGGLE STYLES ============

export const noteToggleSx = (hasNote = false) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem', // 6px
  padding: '0.5rem 0.75rem', // 8px 12px
  marginTop: '1rem', // 16px
  bgcolor: hasNote ? 'rgba(0,184,217,0.06)' : 'transparent',
  border: `1px ${hasNote ? 'solid' : 'dashed'} ${hasNote ? info.main : grey[300]}`,
  borderRadius: callScriptRadius.sm,
  color: hasNote ? info.dark : text.secondary,
  fontSize: '0.8125rem', // 13px
  cursor: 'pointer',
  width: '100%',
  transition: 'all 0.15s',
  '&:hover': {
    borderColor: primary.main,
    color: primary.main,
    bgcolor: 'rgba(0,167,111,0.04)',
  },
});

// ============ SECTION DIVIDER STYLES ============

export const sectionDividerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '1.25rem 0 1rem', // 20px 0 16px
  marginTop: '1.5rem', // 24px
  borderTop: `1px solid ${grey[200]}`,
  '& span': {
    fontSize: '0.9375rem', // 15px
    fontWeight: 600,
    color: text.primary,
  },
  '& small': {
    fontSize: '0.8125rem', // 13px
    color: text.secondary,
    marginTop: '0.125rem', // 2px
  },
};

// ============ SCALE RATING STYLES ============

export const scaleRowSx = {
  display: 'flex',
  gap: '0.5rem', // 8px
  margin: '1rem 0', // 16px 0
};

export const scaleBtnSx = (isSelected = false) => ({
  width: '2.5rem', // 40px
  height: '2.5rem',
  borderRadius: '50%',
  border: `2px solid ${isSelected ? primary.main : grey[300]}`,
  bgcolor: isSelected ? primary.main : background.paper,
  fontSize: '0.875rem', // 14px
  fontWeight: 600,
  color: isSelected ? '#fff' : text.secondary,
  cursor: 'pointer',
  transition: 'all 0.15s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    borderColor: primary.main,
    color: isSelected ? '#fff' : primary.main,
  },
});

// ============ DEFAULT EXPORT ============

export default {
  colors: callScriptColors,
  shadows: callScriptShadows,
  radius: callScriptRadius,
  sectionBadgeColors,
  callTypeConfig,
};
